// src/components/ReturnsAndRefund.jsx
import React, { useMemo } from "react";

import NoticeBox from "./common/NoticeBox";

const ReturnsAndRefund = () => {
  const header = useMemo(
    () => ({
      badge: "Customer Service",
      title: "Returns & Refund Policy",
      description:
        "We want you to be completely satisfied with your purchase. If you're not happy, we're here to help with a simple return and refund process.",
    }),
    []
  );

  const returnableItems = useMemo(
    () => [
      "Unused items in original packaging",
      "Items with all tags and labels attached",
      "Products in resellable condition",
      "Returned within 30 days of delivery",
    ],
    []
  );

  const nonReturnableItems = useMemo(
    () => [
      "Personal care and hygiene products",
      "Opened or used intimate apparel",
      "Perishable goods and food items",
      "Gift cards and downloadable products",
    ],
    []
  );

  const returnSteps = useMemo(
    () => [
      {
        title: "Initiate Your Return",
        description:
          "Log in to your account, go to Order History, select the item you wish to return, and request a return.",
      },
      {
        title: "Print Return Label",
        description:
          "You will receive a prepaid return shipping label by email. Print it and attach it to your package.",
      },
      {
        title: "Ship Your Return",
        description:
          "Drop off your package at an authorized shipping location and keep your receipt for tracking.",
      },
      {
        title: "Receive Your Refund",
        description:
          "After we receive and inspect your return, your refund will be processed within 5 to 7 business days.",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        question: "When will I receive my refund?",
        answer:
          "Refunds are processed within 5 to 7 business days after we receive and inspect your return. Please allow an additional 3 to 5 business days for the credit to appear in your account.",
      },
      {
        question: "What if I lost my receipt?",
        answer:
          "If you ordered online, we can locate your order using your email address or order number. Returns without proof of purchase may be issued as store credit.",
      },
      {
        question: "Can I return sale items?",
        answer:
          "Yes, sale and clearance items can be returned within 30 days for a refund, as long as they meet our return criteria.",
      },
      {
        question: "Do you offer free return shipping?",
        answer:
          "Yes, we provide a prepaid return shipping label for eligible returns within the US.",
      },
    ],
    []
  );

  const notice = useMemo(
    () => ({
      type: "warning",
      icon: "ph-warning",
      title: "Damaged or Defective Item?",
      message:
        "Contact us within 48 hours of delivery at support@example.com with photos of the damage. We will send a replacement or issue a full refund, including return shipping costs.",
    }),
    []
  );

  return (
    <section className="returns-refund">
      <style>
        {`
          .returns-refund {
            padding: 40px 0;
            background: #fafafa;
          }

          .returns-refund .returns-wrapper {
            margin: 0 auto;
          }

          .returns-refund .returns-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .returns-refund .returns-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .returns-refund .returns-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .returns-refund .returns-description {
            max-width: 620px;
            margin: 0 auto;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .returns-refund .returns-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .returns-refund .returns-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 14px;
            color: #222;
            font-weight: 700;
          }

          .returns-refund .returns-list {
            padding-left: 20px;
            margin-bottom: 0;
          }

          .returns-refund .returns-list li {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 8px;
          }

          .returns-refund .returns-list li:last-child {
            margin-bottom: 0;
          }

          .returns-refund .returns-text {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
          }

          .returns-refund .returns-step,
          .returns-refund .returns-faq {
            margin-bottom: 16px;
          }

          .returns-refund .returns-step:last-child,
          .returns-refund .returns-faq:last-child {
            margin-bottom: 0;
          }

          .returns-refund .returns-step-title,
          .returns-refund .returns-faq-title {
            font-size: 16px;
            line-height: 1.4;
            color: #333;
            font-weight: 600;
            margin-bottom: 6px;
          }

          .returns-refund .returns-step-text,
          .returns-refund .returns-faq-text {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
          }

          .returns-refund .notice-small {
            margin-top: 24px;
          }

          @media (max-width: 575px) {
            .returns-refund {
              padding: 30px 0;
            }

            .returns-refund .returns-title {
              font-size: 28px;
            }

            .returns-refund .returns-section {
              padding: 18px;
            }

            .returns-refund .returns-section-title {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="returns-wrapper">
          <div className="returns-header">
            <span className="returns-badge">{header.badge}</span>

            <h1 className="returns-title">{header.title}</h1>

            <p className="returns-description">{header.description}</p>
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">Returnable Items</h3>
            <ul className="returns-list">
              {returnableItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">Non-Returnable Items</h3>
            <ul className="returns-list">
              {nonReturnableItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">How to Return an Item</h3>

            {returnSteps.map((step, index) => (
              <div key={index} className="returns-step">
                <h4 className="returns-step-title">
                  {index + 1}. {step.title}
                </h4>
                <p className="returns-step-text">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">Refund Methods</h3>
            <p className="returns-text">
              Refunds are issued to your original payment method. Credit or debit
              card refunds usually take 5 to 7 business days. PayPal refunds
              usually take 3 to 5 business days. Store credit is issued immediately
              after approval.
            </p>
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">Exchanges</h3>
            <p className="returns-text">
              Need a different size or color? We recommend returning the original
              item for a refund and placing a new order. This helps you receive
              your preferred item faster.
            </p>
          </div>

          <div className="returns-section">
            <h3 className="returns-section-title">Common Questions</h3>

            {faqs.map((faq, index) => (
              <div key={index} className="returns-faq">
                <h4 className="returns-faq-title">{faq.question}</h4>
                <p className="returns-faq-text">{faq.answer}</p>
              </div>
            ))}
          </div>

          <NoticeBox
            type={notice.type}
            icon={notice.icon}
            title={notice.title}
            message={notice.message}
            className="notice-small"
          />
        </div>
      </div>
    </section>
  );
};

export default ReturnsAndRefund;